import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ROTULO_REGIME_CONTRAPARTE, ROTULO_STATUS_PENDENCIA, type RespostaRegime, type StatusPendencia } from '@via-tax/contracts'
import { Aviso, Botao, Entrada, EstadoVazio, Selecao, Selo } from '@via-tax/ui'
import { useState } from 'react'
import { dataBr, reais } from '../formatos'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Caixa de pendências (§4.4). O cliente responde o que só ele sabe; o escritório
 * homologa — a resposta nunca entra direto no número apresentado.
 */

const TOM: Record<StatusPendencia, 'neutro' | 'ok' | 'alerta' | 'navy'> = { ABERTA: 'navy', RESPONDIDA: 'alerta', HOMOLOGADA: 'ok', DEVOLVIDA: 'alerta' }
const OPCOES: Array<{ v: RespostaRegime['regime']; l: string }> = [
  { v: 'SN_HIBRIDO', l: 'Vai apurar IBS/CBS por fora (híbrido)' },
  { v: 'SN_PURO', l: 'Vai continuar só no DAS (puro)' },
  { v: 'REGULAR', l: 'Não é do Simples (Lucro Real/Presumido)' },
  { v: 'MEI', l: 'É MEI' },
  { v: 'NAO_SEI', l: 'Não sei' },
]
/** Pergunta sobre CLIENTE: o que importa é se é do regime regular (aproveita crédito) ou do Simples/MEI. */
const OPCOES_CLIENTE: Array<{ v: RespostaRegime['regime']; l: string }> = [
  { v: 'REGULAR', l: 'Lucro Presumido ou Real (regime regular)' },
  { v: 'SN_PURO', l: 'Simples Nacional' },
  { v: 'SN_HIBRIDO', l: 'Simples, com IBS/CBS por fora (híbrido)' },
  { v: 'MEI', l: 'MEI' },
  { v: 'NAO_SEI', l: 'Não sei' },
]
/** A nota já disse que é do Simples: só falta o que ela não diz. */
const OPCOES_PURO_HIBRIDO: Array<{ v: RespostaRegime['regime']; l: string }> = [
  { v: 'SN_HIBRIDO', l: 'Vai destacar o IBS/CBS na nota (híbrido)' },
  { v: 'SN_PURO', l: 'Vai deixar tudo dentro do DAS (puro)' },
  { v: 'REGULAR', l: 'Na verdade não é do Simples (Real/Presumido)' },
  { v: 'NAO_SEI', l: 'Não sei' },
]
const ROTULO_RESPOSTA = Object.fromEntries([...OPCOES, ...OPCOES_PURO_HIBRIDO].map((o) => [o.v, o.l]))
const ROTULO_RESPOSTA_CLIENTE = Object.fromEntries(OPCOES_CLIENTE.map((o) => [o.v, o.l]))
const ehCliente = (contexto: unknown) => (contexto as { papel?: string } | null)?.papel === 'CLIENTE'
/**
 * A escolha é só puro × híbrido quando a nota da contraparte já disse que ela é do Simples. Perguntas
 * abertas antes de 29/09 não têm `escolha`, mas têm a sugestão: valem pela mesma regra.
 */
const soPuroOuHibrido = (contexto: unknown) => {
  const c = contexto as { escolha?: string; sugestao?: { indica?: string } } | null
  return c?.escolha === 'PURO_OU_HIBRIDO' || (c?.escolha === undefined && c?.sugestao?.indica === 'SIMPLES')
}

export function Pendencias({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [status, setStatus] = useState<StatusPendencia | undefined>()
  const q = useQuery(trpc.pendencia.listar.queryOptions({ status }))
  const caps = contexto.capacidades as string[]
  const homologa = caps.includes('definir_regime_contraparte')
  const responde = caps.includes('responder_pendencia')
  const atualizar = () => {
    void qc.invalidateQueries({ queryKey: trpc.pendencia.pathKey() })
    void qc.invalidateQueries({ queryKey: trpc.contraparte.pathKey() })
  }

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Pendências</h1>
          <p className="mt-1 max-w-2xl text-sm text-tinta-2">
            {contexto.tipoPrincipal === 'CLIENTE'
              ? 'Perguntas do escritório que só a sua empresa sabe responder. As respostas são conferidas pelo escritório antes de entrar na simulação.'
              : 'Perguntas ao cliente. A resposta só vale depois de homologada aqui.'}
          </p>
        </div>
        <Selecao className="w-auto" value={status ?? ''} onChange={(e) => setStatus((e.target.value || undefined) as StatusPendencia | undefined)}>
          <option value="">Todas</option>
          {Object.entries(ROTULO_STATUS_PENDENCIA).map(([k, v]) => (
            <option key={k} value={k}>{v}{q.data?.porStatus[k] ? ` (${q.data.porStatus[k]})` : ''}</option>
          ))}
        </Selecao>
      </header>
      {q.isError && <Aviso>{mensagemDeErro(q.error)}</Aviso>}
      {q.data && q.data.itens.length === 0 ? (
        <EstadoVazio titulo="Nenhuma pendência">
          {contexto.tipoPrincipal === 'CLIENTE' ? 'Nada aguardando a sua resposta.' : 'Abra perguntas pela tela de Fornecedores (regime).'}
        </EstadoVazio>
      ) : (
        <ul className="flex flex-col gap-3">
          {q.data?.itens.map((p) => (
            <li key={p.id} className="rounded-lg border border-linha bg-painel p-4">
              <PendenciaItem p={p} responde={responde} homologa={homologa} aoMudar={atualizar} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface ItemPendencia {
  id: string
  pergunta: string
  status: StatusPendencia
  impactoEstimado: number
  valorProposto?: unknown
  contexto?: unknown
  observacao: string | null
  respondidoPor: string | null
  respondidoEm: Date | string | null
  criadoEm: Date | string
}

function PendenciaItem({ p, responde, homologa, aoMudar }: { p: ItemPendencia; responde: boolean; homologa: boolean; aoMudar: () => void }) {
  const trpc = useTRPC()
  const cliente = ehCliente(p.contexto)
  const puroOuHibrido = soPuroOuHibrido(p.contexto)
  const opcoes = puroOuHibrido ? OPCOES_PURO_HIBRIDO : cliente ? OPCOES_CLIENTE : OPCOES
  const rotulos = cliente && !puroOuHibrido ? ROTULO_RESPOSTA_CLIENTE : ROTULO_RESPOSTA
  const [regime, setRegime] = useState<RespostaRegime['regime']>(puroOuHibrido ? 'SN_HIBRIDO' : cliente ? 'REGULAR' : 'SN_HIBRIDO')
  const [obs, setObs] = useState('')
  const responder = useMutation(trpc.pendencia.responder.mutationOptions({ onSuccess: aoMudar }))
  const homologar = useMutation(trpc.pendencia.homologar.mutationOptions({ onSuccess: aoMudar }))
  const proposta = p.valorProposto as RespostaRegime | null
  const erro = responder.error ?? homologar.error

  return (
    <div className="flex flex-col gap-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Selo tom={TOM[p.status]}>{ROTULO_STATUS_PENDENCIA[p.status]}</Selo>
        <span className="text-[12px] text-tinta-3">aberta em {dataBr(p.criadoEm)}</span>
        {p.impactoEstimado > 0 && <span className="num text-[12px] text-tinta-3">· {cliente ? 'vendas' : 'compras'} no período {reais(p.impactoEstimado)}</span>}
      </div>
      <p>{p.pergunta}</p>
      {proposta && (
        <p className="text-[13px] text-tinta-2">
          Resposta{p.respondidoPor ? ` de ${p.respondidoPor}` : ''}: <strong>{rotulos[proposta.regime] ?? proposta.regime}</strong>
          {proposta.observacao && <> — “{proposta.observacao}”</>}
        </p>
      )}
      {p.observacao && <p className="text-[13px] text-alerta">Escritório: {p.observacao}</p>}

      {responde && (p.status === 'ABERTA' || p.status === 'DEVOLVIDA') && (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            responder.mutate({ pendenciaId: p.id, resposta: { regime, observacao: obs.trim() || undefined } })
          }}
        >
          <Selecao className="w-auto" value={regime} onChange={(e) => setRegime(e.target.value as RespostaRegime['regime'])}>
            {opcoes.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </Selecao>
          <Entrada className="min-w-[220px] flex-1" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Observação (opcional)" />
          <Botao variante="primario" type="submit" disabled={responder.isPending}>Responder</Botao>
        </form>
      )}

      {homologa && p.status === 'RESPONDIDA' && (
        <div className="flex flex-wrap items-center gap-2">
          <Botao
            variante="primario"
            disabled={homologar.isPending || proposta?.regime === 'NAO_SEI'}
            onClick={() => homologar.mutate({ pendenciaId: p.id, aceitar: true })}
            title={proposta?.regime && proposta.regime !== 'NAO_SEI' ? `Confirma o fornecedor como ${ROTULO_REGIME_CONTRAPARTE[proposta.regime]}` : undefined}
          >
            Homologar
          </Botao>
          <Entrada className="min-w-[220px] flex-1" value={obs} onChange={(e) => setObs(e.target.value)} placeholder="Motivo para devolver ao cliente" />
          <Botao variante="perigo" disabled={homologar.isPending || !obs.trim()} onClick={() => homologar.mutate({ pendenciaId: p.id, aceitar: false, observacao: obs.trim() })}>
            Devolver
          </Botao>
        </div>
      )}
      {erro && <Aviso>{mensagemDeErro(erro)}</Aviso>}
    </div>
  )
}
